import { getFullTranscript } from "@/api/endpoints/episodes";
import { EpisodeV4 } from "@/types/MentionCard";
import formatMentionTiming from "@/utils/formatMentionTiming";
import Error from "@components/ui/error";
import { Skeleton } from "@components/ui/skeleton";
import { useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

type FullTranscriptProps = {
  episodePk: EpisodeV4["pk"];
};

type Sentence = {
  time: number;
  text: string;
};

const SENTENCE_END = /[.!?]$/;

function parseSentences(html: string): Sentence[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const spans = doc.querySelectorAll("span[data-time]");

  const sentences: Sentence[] = [];
  let currentTime: number | null = null;
  let currentWords: string[] = [];

  spans.forEach((span) => {
    const word = span.textContent?.trim();
    if (!word) return;

    const time = Number(span.getAttribute("data-time"));
    if (currentTime === null) currentTime = time;
    currentWords.push(word);

    if (SENTENCE_END.test(word)) {
      sentences.push({ time: currentTime, text: currentWords.join(" ") });
      currentTime = null;
      currentWords = [];
    }
  });

  if (currentWords.length > 0 && currentTime !== null) {
    sentences.push({ time: currentTime, text: currentWords.join(" ") });
  }

  return sentences;
}

export default function FullTranscript({ episodePk }: FullTranscriptProps) {
  const { t } = useLingui();

  // Server state
  const { data, isLoading, error } = useQuery({
    queryKey: ["episodes", episodePk, "full-transcript"],
    queryFn: () => getFullTranscript(episodePk),
  });

  const sentences = useMemo(() => {
    if (!data?.ok) return [];
    return parseSentences(data.data);
  }, [data]);

  // Helpers
  function renderError() {
    return (
      <Error
        heading={t`Åh nej!`}
        description={t`Något gick snett med att ladda hela transkriptet. Vänligen försök igen senare`}
      />
    );
  }

  return data ? (
    data.ok ? (
      sentences.length > 0 ? (
        <div className="space-y-2 text-sm leading-relaxed">
          {sentences.map((sentence, index) => (
            <div key={index} className="flex gap-3">
              <span className="text-muted-foreground shrink-0 tabular-nums">
                {formatMentionTiming(sentence.time)}
              </span>
              <p>{sentence.text}</p>
            </div>
          ))}
        </div>
      ) : (
        <p
          className="text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: data.data }}
        />
      )
    ) : (
      renderError()
    )
  ) : isLoading ? (
    <div className="space-y-4">
      <Skeleton className="h-9" />
      <Skeleton className="h-9" />
      <Skeleton className="h-9" />
    </div>
  ) : error ? (
    renderError()
  ) : null;
}
